import {
  Dropdown,
  type DropdownGroup,
  type DropdownOption,
} from "@freightsimple/ui-core/Controls/Dropdown";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import type { DataTestProps } from "@freightsimple/shared/types";
import type { UUID } from "@/Shared/types";
import { LocationsFavouriteMarker } from "@/Shared/Locations/CustomComponents/LocationsFavouriteMarker";
import { LocationsBranchMarker } from "@/Shared/Locations/CustomComponents/LocationsBranchMarker";
import { LocationsDefaultMarker } from "@/Shared/Locations/CustomComponents/LocationsDefaultMarker";
import { fullStateOrProvinceName } from "@/Helpers/fullStateOrProvinceName";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { formatPostalCode } from "@/Helpers/formatPostalCode";

interface SavedLocationsDropdownProps extends DataTestProps {
  savedLocationId: UUID;
  onSetSavedLocationId: (locationId: UUID, contactId: UUID) => void;
  savedLocations: Array<SavedLocation>;
  onAddLocation: () => void;
  defaultLocationId: undefined | UUID;
  context: LocationContext;
}

const FAVOURITES = "Favourite Locations";
const BRANCHES = "Branch Locations";
const OTHER = "Other Locations";

function AddressBookHint_({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children} in your{" "}
      <Link href="/address-book" external>
        address book
      </Link>
    </>
  );
}

const BookShipmentSavedLocationsDropdown: React.FC<
  SavedLocationsDropdownProps
> = (props: SavedLocationsDropdownProps) => {
  const defaultGroup = `Default ${props.context}`;

  function locationSearchText(sl: SavedLocation): string {
    // The address fields Dropdown's search matches against, since the option's
    // visible label is only the business name.
    const a = sl.location.address;
    return [
      a.addressLine,
      a.addressLine2,
      a.city,
      a.postalCode,
      a.stateOrProvinceCode,
      fullStateOrProvinceName(a.stateOrProvinceCode),
    ]
      .filter(Boolean)
      .join(" ");
  }

  function summarizeAddress(location: Location): string {
    if (!location.address) {
      throw new Error("Missing location.address");
    }
    const a = location.address;
    return `${a.city}, ${formatPostalCode(a.postalCode)}, ${a.stateOrProvinceCode}`;
  }

  function locationOption(sl: SavedLocation, group: string): DropdownOption {
    return {
      label: sl.location.businessName ?? "",
      value: sl.savedLocationId,
      description: summarizeAddress(sl.location),
      // Findable by street and full province name too, neither of which the
      // row shows.
      searchText: locationSearchText(sl),
      group,
    };
  }

  function isDefault(sl: SavedLocation) {
    return (
      props.defaultLocationId !== undefined &&
      sl.savedLocationId === props.defaultLocationId
    );
  }

  function options(): DropdownOption[] {
    const locations = props.savedLocations ?? [];
    const notDefault = locations.filter((sl) => !isDefault(sl));

    return [
      ...locations
        .filter(isDefault)
        .map((sl) => locationOption(sl, defaultGroup)),
      ...notDefault
        .filter((sl) => sl.isFavourite)
        .map((sl) => locationOption(sl, FAVOURITES)),
      ...notDefault
        .filter((sl) => sl.isBranch)
        .map((sl) => locationOption(sl, BRANCHES)),
      ...notDefault
        .filter((sl) => !sl.isFavourite && !sl.isBranch)
        .map((sl) => locationOption(sl, OTHER)),
    ];
  }

  // Declared up front so Default / Favourites / Branches keep their order and
  // still say something when empty. "Other" is not declared: an empty one has
  // nothing worth explaining, so it just doesn't appear.
  const groups: DropdownGroup[] = [
    {
      name: defaultGroup,
      icon: <LocationsDefaultMarker />,
      emptyMessage: (
        <AddressBookHint_>
          You do not have a default {props.context.toLowerCase()} location set
        </AddressBookHint_>
      ),
    },
    {
      name: FAVOURITES,
      icon: <LocationsFavouriteMarker />,
      emptyMessage: (
        <AddressBookHint_>
          You do not have any locations marked as favourite
        </AddressBookHint_>
      ),
    },
    {
      name: BRANCHES,
      icon: <LocationsBranchMarker />,
      emptyMessage: (
        <AddressBookHint_>
          You do not have any locations marked as branches
        </AddressBookHint_>
      ),
    },
  ];

  function onOptionSelected(savedLocationId: string) {
    const location = props.savedLocations.find(
      (l) => l.savedLocationId === savedLocationId,
    );
    if (location === undefined) {
      throw new Error("Missing location");
    }

    props.onSetSavedLocationId(savedLocationId, location.defaultSavedContactId);
  }

  return (
    <Row gap={4} align="center">
      <Dropdown
        data-testid={props["data-testid"]}
        value={props.savedLocationId}
        onChange={onOptionSelected}
        options={options()}
        groups={groups}
        placeholder="Select an address"
        searchable
        searchPlaceholder="Type to filter locations"
        width="xl"
      />
      <Link onClick={props.onAddLocation}>or enter a new postal code</Link>
    </Row>
  );
};
export default BookShipmentSavedLocationsDropdown;
